import { useEffect, useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { submitInvestorRequest } from "@/lib/investor-leads.functions";
import { captureAttribution, getAttribution } from "@/lib/attribution";
import { trackEvent } from "@/lib/analytics";

const schema = z.object({
  name: z.string().trim().min(2, "Name is required").max(120),
  email: z.string().trim().email("Valid email required").max(200),
  phone: z.string().trim().min(6, "Phone is required").max(40),
  company: z.string().trim().min(1, "Company / fund required").max(200),
  investor_type: z.string().min(1, "Select investor type"),
  estimated_interest: z.string().min(1, "Select investment interest"),
  message: z.string().max(2000).optional(),
});

const types = [
  "Angel Investor", "Venture Capital", "Private Equity",
  "Strategic Partner", "Franchise Developer", "Sports Team / Venue", "Other",
];
const amounts = ["Under $100K", "$100K–$250K", "$250K–$500K", "$500K–$1M", "$1M+"];

export function InvestorForm() {
  const submit = useServerFn(submitInvestorRequest);
  const [submitted, setSubmitted] = useState<null | { emailSent: boolean }>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  const onFirstInteraction = () => {
    if (started) return;
    setStarted(true);
    trackEvent("investor_form_start", { form: "investor_deck_request" });
  };

  useEffect(() => {
    captureAttribution();
  }, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setServerError(null);
    const fd = new FormData(e.currentTarget);
    const website = String(fd.get("website") || "");
    const data = {
      name: String(fd.get("name") || ""),
      email: String(fd.get("email") || ""),
      phone: String(fd.get("phone") || ""),
      company: String(fd.get("company") || ""),
      investor_type: String(fd.get("investor_type") || ""),
      estimated_interest: String(fd.get("estimated_interest") || ""),
      message: String(fd.get("message") || ""),
    };
    const res = schema.safeParse(data);
    if (!res.success) {
      const err: Record<string, string> = {};
      for (const i of res.error.issues) err[String(i.path[0])] = i.message;
      setErrors(err);
      return;
    }
    setErrors({});
    setSubmitting(true);
    trackEvent("investor_form_submit", { form: "investor_deck_request" });
    try {
      const result = await submit({ data: { ...res.data, website, ...getAttribution() } });
      if (!result.ok) {
        setServerError(result.error || "Something went wrong. Please try again.");
      } else {
        trackEvent("investor_request_success", { form: "investor_deck_request" });
        setSubmitted({ emailSent: !!result.emailSent });
      }
    } catch (err) {
      setServerError((err as Error).message || "Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full bg-background border border-border rounded-sm px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition";
  const label = "block text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2";

  return (
    <section id="invest" className="relative py-32 bg-arena">
      <div className="mx-auto max-w-4xl px-6">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Investor Access</p>
          <h2 className="font-display text-4xl sm:text-6xl leading-tight">
            Request the <span className="text-gradient-ember">ClutchCube</span> Investor Deck
          </h2>
          <p className="mt-6 text-muted-foreground max-w-xl mx-auto">
            Confidential materials, projections, and flagship location plan available on request.
          </p>
        </div>

        {submitted !== null ? (
          <div className="relative bg-card border border-primary/40 rounded-sm p-12 text-center shadow-glow">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 border border-primary mb-6">
              <span className="text-primary text-2xl">✓</span>
            </div>
            <h3 className="font-display text-3xl">Thank You.</h3>
            <p className="mt-4 text-muted-foreground max-w-md mx-auto">
              {submitted.emailSent
                ? "Check your inbox — we've just sent your secure link to the ClutchCube 2026 investor deck. Our team will follow up shortly."
                : "Your request has been received. We're preparing your investor materials and will send them to you shortly."}
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} onFocus={onFirstInteraction} className="relative bg-card border border-border rounded-sm p-8 sm:p-10 shadow-elevated">
            {/* Honeypot: hidden from users, bots fill it in */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
              <label htmlFor="website">Website</label>
              <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className={label} htmlFor="name">Full Name</label>
                <input id="name" name="name" className={field} placeholder="Jane Smith" />
                {errors.name && <p className="mt-2 text-xs text-destructive">{errors.name}</p>}
              </div>
              <div>
                <label className={label} htmlFor="email">Email</label>
                <input id="email" name="email" type="email" className={field} placeholder="jane@fund.com" />
                {errors.email && <p className="mt-2 text-xs text-destructive">{errors.email}</p>}
              </div>
              <div>
                <label className={label} htmlFor="phone">Phone</label>
                <input id="phone" name="phone" className={field} placeholder="+1 (555) 000-0000" />
                {errors.phone && <p className="mt-2 text-xs text-destructive">{errors.phone}</p>}
              </div>
              <div>
                <label className={label} htmlFor="company">Company / Fund</label>
                <input id="company" name="company" className={field} placeholder="Capital Partners" />
                {errors.company && <p className="mt-2 text-xs text-destructive">{errors.company}</p>}
              </div>
              <div>
                <label className={label} htmlFor="investor_type">Investor Type</label>
                <select id="investor_type" name="investor_type" defaultValue="" className={field}>
                  <option value="" disabled>Select type…</option>
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {errors.investor_type && <p className="mt-2 text-xs text-destructive">{errors.investor_type}</p>}
              </div>
              <div>
                <label className={label} htmlFor="estimated_interest">Estimated Investment Interest</label>
                <select id="estimated_interest" name="estimated_interest" defaultValue="" className={field}>
                  <option value="" disabled>Select range…</option>
                  {amounts.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                {errors.estimated_interest && <p className="mt-2 text-xs text-destructive">{errors.estimated_interest}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className={label} htmlFor="message">Message / Notes</label>
                <textarea id="message" name="message" rows={4} className={field} placeholder="Anything we should know…" />
              </div>
            </div>
            {serverError && <p className="mt-6 text-sm text-destructive text-center">{serverError}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="mt-8 w-full px-8 py-4 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-sm rounded-sm shadow-glow hover:translate-y-[-2px] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Request the ClutchCube Investor Deck"}
            </button>
            <p className="mt-4 text-center text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Confidential · Reviewed within 48 hours
            </p>
            <p className="mt-6 text-center text-xs text-muted-foreground leading-relaxed max-w-xl mx-auto">
              By submitting this form you acknowledge that you have read and agree to our{" "}
              <Link to="/terms" className="text-primary hover:underline">
                Terms of Use
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
